import { TxStatus } from "@aztec/aztec.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { doShield, doUnshield } from "../actions/shielding";
import { doPrivateTransfer, doPublicTransfer } from "../actions/transfers";
import type { ShieldingParams, ShieldingTxFailed, ShieldingTxSuccess } from "../components/shielding/shieldingConfig";
import type { TransferParams, TransferTxFailed, TransferTxSuccess } from "../components/transfers/transfersConfig";
import { storeShieldingTx } from "../state/shieldingTxHistory";
import { storeTransferTx } from "../state/transferTxHistory";

export function useTransfersMutation({
  onSuccess,
  onError,
  onTxSubmitted,
}: {
  onSuccess: (transferSuccess: TransferTxSuccess) => void;
  onError: (transferError: TransferTxFailed) => void;
  onTxSubmitted?: (txHash: string) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (transferParams: TransferParams) => {
      if (transferParams.transferMode === "public") {
        return await doPublicTransfer(
          transferParams.tokenAddress,
          transferParams.recipientAddress,
          transferParams.amount,
          onTxSubmitted,
        );
      }

      return await doPrivateTransfer(
        transferParams.tokenAddress,
        transferParams.recipientAddress,
        transferParams.amount,
        onTxSubmitted,
      );
    },
    onSuccess: (txHash, transferParams) => {
      if (transferParams.transferMode === "public") {
        queryClient.invalidateQueries({
          queryKey: [
            "l2_account",
            transferParams.l2AccountAddress.toString(),
            "public_balance",
            transferParams.tokenAddress.toString(),
          ],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [
            "l2_account",
            transferParams.l2AccountAddress.toString(),
            "private_balance",
            transferParams.tokenAddress.toString(),
          ],
        });
      }

      const transferSuccess: TransferTxSuccess = {
        ...transferParams,
        txStatus: "success",
        txHash,
        timestamp: Date.now(),
      };

      storeTransferTx(transferSuccess);

      onSuccess?.(transferSuccess);
    },
    onError: (error, transferParams) => {
      console.error(error);

      const id = crypto.randomUUID();
      const timestamp = Date.now();

      const transferError: TransferTxFailed = {
        txStatus: "failed",
        id,
        timestamp,
        errorMessage: error.message,
        transferParams,
      };

      storeTransferTx(transferError);

      onError(transferError);
    },
  });
}

export function useShieldingMutation({
  onSuccess,
  onError,
  onTxSubmitted,
}: {
  onSuccess: (shieldingResult: ShieldingTxSuccess) => void;
  onError: (shieldingError: ShieldingTxFailed) => void;
  onTxSubmitted?: (txHash: string) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (shieldingParams: ShieldingParams) => {
      if (shieldingParams.shieldingMode === "shield") {
        const tx = await doShield(shieldingParams.tokenAddress, shieldingParams.amount);
        const txHash = (await tx.getTxHash()).toString();
        onTxSubmitted?.(txHash);
        const receipt = await tx.wait();
        if (receipt.status !== TxStatus.SUCCESS) {
          throw new Error("Shielding transaction failed");
        }
        return txHash;
      }

      const tx = await doUnshield(shieldingParams.tokenAddress, shieldingParams.amount);
      const txHash = (await tx.getTxHash()).toString();
      onTxSubmitted?.(txHash);
      const receipt = await tx.wait();
      if (receipt.status !== TxStatus.SUCCESS) {
        throw new Error("Unshielding transaction failed");
      }
      return txHash;
    },
    onSuccess: (txHash, shieldingParams) => {
      // Invalidate specific public balance
      queryClient.invalidateQueries({
        queryKey: ["public-balance", shieldingParams.tokenAddress.toString(), shieldingParams.l2AccountAddress.toString()],
      });

      // Invalidate specific private balance
      queryClient.invalidateQueries({
        queryKey: ["private-balance", shieldingParams.tokenAddress.toString(), shieldingParams.l2AccountAddress.toString()],
      });

      const timestamp = Date.now();

      const shieldingSuccess: ShieldingTxSuccess = {
        ...shieldingParams,
        txStatus: "success",
        txHash,
        timestamp,
      };

      storeShieldingTx(shieldingSuccess);

      onSuccess(shieldingSuccess);
    },
    onError: (error, shieldingParams) => {
      console.error(error);

      const id = crypto.randomUUID();
      const timestamp = Date.now();

      const shieldingError: ShieldingTxFailed = {
        txStatus: "failed",
        id,
        timestamp,
        errorMessage: error.message,
        shieldingParams,
      };

      storeShieldingTx(shieldingError);

      onError(shieldingError);
    },
  });
}
